import { Components, Theme, alpha } from '@mui/material/styles';

/**
 * MELP Mail component overrides.
 *
 * Conventions:
 *   - Radii: read from `theme.shape.borderRadius` (set in createMelpTheme).
 *     Never hardcode `8`; if a design wants a different radius for a single
 *     surface, multiply the token (e.g. `theme.shape.borderRadius * 0.5`).
 *   - Shadows: read from `theme.shadows[n]` so light/dark themes can pick
 *     different elevations. Hardcoded rgba shadows look heavy in dark mode.
 *   - Hover/selected states: build with `alpha(theme.palette.X.main, n)` so
 *     they remain readable across both modes.
 */
export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => {
      const isDark = theme.palette.mode === 'dark';
      // Thin, theme-aware scrollbars on every scrollable container in the
      // app. Without this the OS default chrome shows through — chunky
      // gray bars on Linux/Windows that look out of place against the
      // app's spacing and especially jarring in dark mode (the bar stays
      // light against the dark surface).
      //
      // Firefox uses scrollbar-width/scrollbar-color on the element.
      // WebKit/Blink uses the ::-webkit-scrollbar pseudos. Both are
      // applied at the * level so any overflow:auto container picks
      // them up without each component needing its own rules.
      // Barely-there scrollbar. Visible enough to grab when you want it,
      // invisible enough that a quiet inbox doesn't draw the eye to a
      // chrome bar on the right edge.
      const thumb = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.12)';
      const thumbHover = isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.30)';
      const track = 'transparent';
      return {
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          scrollbarWidth: 'thin',
          scrollbarColor: `${thumb} ${track}`,
        },
        body: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${thumb} ${track}`,
        },
        '*::-webkit-scrollbar': {
          width: 6,
          height: 6,
          backgroundColor: track,
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: track,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: thumb,
          borderRadius: 4,
          transition: 'background-color 120ms ease-out',
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: thumbHover,
        },
        '*::-webkit-scrollbar-corner': {
          backgroundColor: track,
        },
        // Make Material Symbols Rounded the default for any <span class="material-symbols-rounded">.
        '.material-symbols-rounded': {
          fontFamily: '"Material Symbols Rounded", sans-serif',
          fontWeight: 400,
          fontStyle: 'normal',
          fontSize: 24,
          lineHeight: 1,
          letterSpacing: 'normal',
          textTransform: 'none',
          display: 'inline-block',
          whiteSpace: 'nowrap',
          wordWrap: 'normal',
          direction: 'ltr',
          WebkitFontFeatureSettings: '"liga"',
          WebkitFontSmoothing: 'antialiased',
        },
      };
    },
  },
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        textTransform: 'none',
        fontWeight: 500,
        padding: '8px 16px',
      }),
      contained: ({ theme }) => ({
        boxShadow: 'none',
        '&:hover': {
          boxShadow: theme.shadows[2],
        },
      }),
      sizeSmall: {
        padding: '4px 12px',
      },
      sizeLarge: {
        padding: '10px 22px',
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        transition: theme.transitions.create(['background-color', 'color'], {
          duration: theme.transitions.duration.shortest,
        }),
      }),
      sizeSmall: { padding: 6 },
      sizeMedium: { padding: 8 },
      sizeLarge: { padding: 10 },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[1],
        backgroundImage: 'none',
      }),
    },
  },
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        backgroundImage: 'none',
      }),
      outlined: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          borderRadius: theme.shape.borderRadius,
        },
      }),
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
      }),
      notchedOutline: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        fontWeight: 500,
      }),
      sizeSmall: {
        height: 22,
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius * 1.5,
        backgroundImage: 'none',
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        fontSize: '1.125rem',
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
      }),
      standardSuccess: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
      }),
      standardError: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
      }),
      standardWarning: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
      }),
      standardInfo: ({ theme }) => ({
        backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.08),
      }),
    },
  },
  MuiAppBar: {
    defaultProps: {
      elevation: 0,
      color: 'inherit',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundImage: 'none',
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
      }),
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: 0,
        backgroundImage: 'none',
        borderRight: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        margin: '2px 8px',
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
          },
        },
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        minHeight: 44,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 44,
      },
      indicator: ({ theme }) => ({
        height: 3,
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
      }),
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius * 0.5,
        fontSize: '0.75rem',
        fontWeight: 500,
        padding: '6px 10px',
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.grey[200], 0.95)
          : alpha(theme.palette.grey[900], 0.92),
        color: theme.palette.mode === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.common.white,
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.mode === 'dark'
          ? alpha(theme.palette.grey[200], 0.95)
          : alpha(theme.palette.grey[900], 0.92),
      }),
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
      }),
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius * 0.5,
        margin: '2px 6px',
        minHeight: 36,
        '&.Mui-selected': {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
        },
      }),
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius,
        height: 6,
      }),
    },
  },
};
